<script setup lang="ts">
import { Listbox, ListboxLabel, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import { useField } from "vee-validate";
import type { SelectItem } from "@/types";

interface Props {
  modelValue?: SelectItem;
  items: SelectItem[];
  label?: string;
  name: string;
  validateAs?: string;
  placeholder?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  showCheckmark?: boolean;
  openUpwards?: boolean | "auto";
  size?: "sm" | "md";
}

interface Emit {
  (event: "update:modelValue", value: SelectItem | null): void;
}

const emit = defineEmits<Emit>();
const props = withDefaults(defineProps<Props>(), {
  showCheckmark: true,
  openUpwards: "auto",
  size: "md",
});

const { errorMessage } = useField(() => props.name, props.validateAs, {
  syncVModel: true,
});

const selectValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", props.allowEmpty && value?.id === props.modelValue?.id ? null : value ?? null),
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case "sm":
      return "min-h-[1.8rem] pl-3 pr-10 py-px leading-6 text-xs";
    case "md":
    default:
      return "min-h-[2.4rem] pl-3 pr-10 py-2 leading-6 text-sm";
  }
});

// Auto-positioning logic
const buttonRef = ref<HTMLElement | null>(null);
const shouldOpenUpwards = ref(false);

// Calculate if dropdown should open upwards based on available space
const calculatePosition = () => {
  if (props.openUpwards === true) {
    shouldOpenUpwards.value = true;
    return;
  }
  if (props.openUpwards === false) {
    shouldOpenUpwards.value = false;
    return;
  }

  // Auto mode: calculate based on available space
  if (!buttonRef.value) return;

  const rect = buttonRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Dropdown max height is 15rem (240px) + some padding
  const dropdownHeight = 260;

  // Open upwards if not enough space below but enough above
  shouldOpenUpwards.value = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
};

const clearInput = () => {
  emit("update:modelValue", null);
};

defineExpose({
  clearInput,
});
</script>

<template>
  <div v-auto-animate>
    <Listbox
      v-model="selectValue"
      :disabled="disabled"
      as="div"
      by="id"
      class="relative text-dark-gray"
      :class="{ 'opacity-50': disabled }"
      v-slot="{ open }"
    >
      <ListboxLabel v-if="label" class="px-1 text-xs" :class="{ 'text-primary': open }">
        {{ label }}
      </ListboxLabel>
      <div class="relative" ref="buttonRef" @click="calculatePosition">
        <ListboxButton
          class="border cursor-default relative rounded-md text-left w-full bg-white focus:outline-primary"
          :class="[sizeClasses, { 'border-primary shadow-md': open }, errorMessage ? 'border-danger' : 'border-light-gray']"
        >
          <span class="block truncate" :class="{ 'text-medium-gray': !selectValue?.name }">{{ selectValue?.name ?? placeholder }}</span>
          <span class="absolute right-0 inset-y-0 flex items-center pointer-events-none pr-2">
            <Icon
              name="fa6-solid:chevron-down"
              :class="{ 'rotate-180': open }"
              class="h-3 w-3 text-medium-gray transition-transform"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>

        <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
          <ListboxOptions
            :class="[
              'absolute bg-white max-h-60 overflow-auto p-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg w-full z-[60] focus:outline-none text-sm',
              shouldOpenUpwards ? 'mb-1 bottom-full' : 'mt-1 top-full',
            ]"
          >
            <ListboxOption v-for="item in items" :key="item.id" :value="item" v-slot="{ active, selected }">
              <li
                class="cursor-default pr-4 py-2 relative select-none rounded-md flex items-center"
                :class="[{ 'bg-primary text-white': active || (!showCheckmark && selected) }, showCheckmark ? 'pl-10' : 'pl-4']"
              >
                <span class="block font-normal truncate ui-selected:font-medium w-full" :class="{ 'font-medium': selected }">
                  <div v-if="$slots.listItem">
                    <slot name="listItem" :item="item" :active="active" :selected="selected" />
                  </div>
                  <div v-else class="flex items-center">
                    <div>{{ item.name }}</div>
                    <span v-if="item.hasIndicator" class="ml-5 h-2.5 w-2.5 bg-danger rounded-full"></span>
                  </div>
                </span>
                <span
                  v-if="showCheckmark"
                  class="absolute left-0 inset-y-0 items-center text-primary"
                  :class="[
                    selected ? 'flex' : 'hidden',
                    { 'text-white': active },
                    { 'pl-3': showCheckmark },
                    { 'justify-center': !showCheckmark },
                  ]"
                >
                  <Icon name="fa6-solid:check" class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
    <InputError v-if="errorMessage">{{ errorMessage }}</InputError>
  </div>
</template>
